import {AddSchema} from "mobx-graphlink";
import {TriggerSet} from "../@TriggerSet.js";
import {EffectPointer} from "../../@Shared/EffectPointer.js";
import {GetValues_ForSchema} from "js-vextensions";

export class EngineConfig_Breath {
	constructor(data: Partial<EngineConfig_Breath>) { Object.assign(this, data); }

	enabled: boolean;
	recordData_raw: boolean;
	maxSampleGapInSequence_base: number; // in seconds

	// sync
	// ==========

	sync_enabled: boolean;
	sync_increaseCycleTime_triggerSet: TriggerSet;
	sync_decreaseCycleTime_triggerSet: TriggerSet;
	sync_increaseMinVolume_triggerSet: TriggerSet;
	sync_decreaseMinVolume_triggerSet: TriggerSet;
	sync_blockBreathIntervalEffects_triggerSet: TriggerSet;
	sync_blockBreathIntervalEffects_duration: number; // in minutes

	//voice_soundTag: string;
	voice_volumeMultiplier: number;
	sync_snoozeFadeOffDuration: number; // in seconds

	// spatial (ie. panning of audio)
	// ----------

	spatial_enabled: boolean;
	spatial_minBreathValueReversal: number; // in raw respiration-belt value units (0 = disabled)
	spatial_inhalePanDirection: PanDirection;
	spatial_panStrength: number; // 0-1; how much volume reduction to apply to the "off" ear (1 = fully silent, 0 = no panning effect)
	spatial_inhaleMuted: boolean;

	// drifting mode
	// ----------

	// desync calculation
	sync_desyncCalcType: DesyncCalcType;
	sync_minBreathPeakProminence: number; // in absolute value (ie. raw value received from respiration-belt)
	sync_minBreathPeakHeight: number; // in absolute value (ie. raw value received from respiration-belt)
	sync_responseTime: number; // in seconds
	sync_calcDesyncOverXSeconds_long: number;
	sync_calcDesyncOverXSeconds_short: number;

	// siren
	sync_cycleTime: number; // in seconds
	sync_cycleTime_step: number;
	sync_siren_cycleCorrectionInterval: number; // in seconds
	sync_sirenType: SirenType;
	// (subsection below only relevant if using siren-type "siren")
	sync_volume_min: number; // 0-1
	sync_volume_min_step: number;
	sync_volume_max: number; // 0-1
	sync_desyncAmountForMinVolume: number; // 0-1
	sync_desyncAmountForMaxVolume: number; // 0-1
	// (subsection below only relevant if using siren-type "memory palace prompt")
	sync_memPrompt_interval: number; // in seconds
	sync_memPrompt_volumeSequence: number[]; // each entry 0-1

	// light
	//sync_light_effectPointer = new EffectPointer();
	sync_light_brightness_min: number; // 0-1
	sync_light_brightness_max: number; // 0-1
	sync_light_desyncAmountForMinBrightness: number; // 0-1
	sync_light_desyncAmountForMaxBrightness: number; // 0-1

	// dreaming mode
	// ----------

	dream_breathIntervalThreshold: number;
	dream_endWhenBreathIntervalAbove: number;
	dream_startWhenBreathIntervalLowRangeBelow: number;
	dream_endWhenBreathIntervalLowRangeAbove: number;
	dream_ender3_triggerBreathIntervalLowRange: number;
	dream_ender3_stayAboveBreathIntervalLowRange: number;
	dream_ender3_stayAboveDuration: number; // in minutes
	dream_endAfter: number; // in minutes
	dream_light_brightness: number; // 0-1

	// long-inhale (config for detection of this event-type, as referenced in trigger-sets)
	// ----------

	longInhaleDetectionConfigs: LongInhaleDetectionConfig[];

	// DEILD attempt start
	// ----------

	//deild_attemptStart_type: DeildAttemptStartType;
	deild_attemptStart_triggerSet: TriggerSet;
	deild_attemptStart_extraEffect: EffectPointer;
	deild_attemptDuration: number; // in minutes

	// alerts
	// ----------

	alert_beltDC_enabled: boolean;
	alert_beltDC_lockLightsToBrightness: number; // 0-1
	alert_beltLoose_enabled: boolean;
	alert_beltLoose_detectionStart: number; // in minutes
	alert_beltLoose_warnInterval: number; // in seconds
	alert_beltLoose_lockLightsAfter: number; // in minutes
	alert_beltLoose_lockLightsToBrightness: number; // 0-1
}
AddSchema("EngineConfig_Breath", {
	properties: {
		enabled: {type: "boolean"},
		recordData_raw: {type: "boolean"},

		sync_enabled: {type: "boolean"},
		sync_increaseCycleTime_triggerSet: {$ref: "TriggerSet"},
		sync_decreaseCycleTime_triggerSet: {$ref: "TriggerSet"},
		sync_increaseMinVolume_triggerSet: {$ref: "TriggerSet"},
		sync_decreaseMinVolume_triggerSet: {$ref: "TriggerSet"},

		voice_volumeMultiplier: {type: "number"},
		sync_snooze_duration: {type: "number"},

		// ----------

		spatial_enabled: {type: "boolean"},
		spatial_minBreathValueReversal: {type: "number"},
		spatial_inhalePanDirection: {$ref: "PanDirection"},
		spatial_panStrength: {type: "number"},
		spatial_inhaleMuted: {type: "boolean"},

		// ----------

		sync_desyncCalcType: {$ref: "DesyncCalcType"},
		sync_responseTime: {type: "number"},
		sync_calcDesyncOverXSeconds_long: {type: "number"},
		sync_calcDesyncOverXSeconds_short: {type: "number"},

		sync_cycleTime: {type: "number"},
		sync_cycleTime_step: {type: "number"},
		sync_siren_cycleCorrectionInterval: {type: "number"},
		sync_minBreathPeakProminence: {type: "number"},
		sync_minBreathPeakHeight: {type: "number"},
		sync_volume_min: {type: "number"},
		sync_volume_min_step: {type: "number"},
		sync_volume_max: {type: "number"},
		sync_desyncAmountForMinVolume: {type: "number"},
		sync_desyncAmountForMaxVolume: {type: "number"},

		//sync_light_effectPointer: {$ref: "EffectPointer"},
		sync_light_brightness_min: {type: "number"},
		sync_light_brightness_max: {type: "number"},
		sync_light_desyncAmountForMinBrightness: {type: "number"},
		sync_light_desyncAmountForMaxBrightness: {type: "number"},

		// ----------

		dream_breathIntervalThreshold: {type: "number"},
		dream_endWhenBreathIntervalAbove: {type: "number"},
		dream_startWhenBreathIntervalLowRangeBelow: {type: "number"},
		dream_endWhenBreathIntervalLowRangeAbove: {type: "number"},
		dream_ender3_triggerBreathIntervalLowRange: {type: "number"},
		dream_ender3_stayAboveBreathIntervalLowRange: {type: "number"},
		dream_ender3_stayAboveDuration: {type: "number"},
		dream_endAfter: {type: "number"},
		dream_light_brightness: {type: "number"},

		// ----------

		longInhaleDetectionConfigs: {type: "array", items: {$ref: "LongInhaleDetectionConfig"}},

		// ----------

		deild_attemptStart_triggerSet: {$ref: "TriggerSet"},
		deild_attemptStart_extraEffect: {$ref: "EffectPointer"},
		deild_attemptDuration: {type: "number"},

		// ----------

		beltDisconnect_lockLightsToBrightness: {type: "number"},
		beltLoose_detectionStart: {type: "number"},
		beltLoose_warnInterval: {type: "number"},
		beltLoose_lockLightsAfter: {type: "number"},
		beltLoose_lockLightsToBrightness: {type: "number"},
	},
});

export class LongInhaleDetectionConfig {
	constructor(data: Partial<LongInhaleDetectionConfig>) { Object.assign(this, data); }
	minDuration: number; // in seconds
	minDuration_phaseDreaming: number; // in seconds
	minAscendHeight: number; // in raw respiration-belt value units
	maxRoughness: number; // in ? units (based on roughness function, operating on respiration-belt values)
}
AddSchema("LongInhaleDetectionConfig", {
	properties: {
		minDuration: {type: "number"},
		minDuration_phaseDreaming: {type: "number"},
		minAscendHeight: {type: "number"},
		maxRoughness: {type: "number"},
	},
});

export enum PanDirection {
	left = "left",
	right = "right",
}
AddSchema("PanDirection", {oneOf: GetValues_ForSchema(PanDirection)});

export enum DesyncCalcType {
	peaks = "peaks",
	slopes = "slopes",
}
AddSchema("DesyncCalcType", {oneOf: GetValues_ForSchema(DesyncCalcType)});

export enum SirenType {
	siren = "siren",
	memoryPalacePrompt = "memoryPalacePrompt",
}
AddSchema("SirenType", {oneOf: GetValues_ForSchema(SirenType)});